import React, { useState, useEffect, useMemo, useCallback } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import { useTranslation } from "react-i18next";

const TradesTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const [tableState, setTableState] = useState({
    trades: [],
    isLoading: true,
  });

  const {
    product,
    trades: propsTrades,
    marketDate,
    priceBasisId,
    currencyId,
    uomId,
    organizaitonId,
    currencySymbol,
    pricePrecision,
    volumePrecision,
    onRowClick,
  } = props;

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  // Map to view model
  const mapToViewModel = useCallback(
    (trades) => {
      if (!trades || !product) return [];

      const dateFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "numeric",
      };

      return trades.map((trade) => ({
        ...{ shortName: product.shortName },
        ...trade,
        ...{
          tradeDateFormatted: new Date(trade.tradeDate).toLocaleDateString(
            "en-US",
            dateFormatOptions
          ),
          priceFormatted:
            currencySymbol + Number(trade.price).toFixed(pricePrecision),
          volumeFormatted: Number(trade.volume).toFixed(volumePrecision),
        },
        ...trade.qualityAttributeValues.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      }));
    },
    [product, currencySymbol, pricePrecision, volumePrecision]
  );

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    if (!product) return [];

    return [
      { field: "shortName", headerName: t("MarketDataProduct"), flex: 1 },
      ...product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          flex: 1,
        })),
      { field: "termName", headerName: t("Term"), flex: 1 },
      {
        field: "buyerOrganizationName",
        headerName: t("BuyerOrganization"),
        flex: 1,
      },
      {
        field: "sellerOrganizationName",
        headerName: t("SellerOrganization"),
        flex: 1,
      },
      { field: "priceBasisName", headerName: t("PriceBasis"), flex: 1 },
      { field: "priceFormatted", headerName: t("Price"), flex: 1 },
      { field: "volumeFormatted", headerName: t("Volume"), flex: 1 },
      { field: "tradeDateFormatted", headerName: t("TradeDate"), flex: 1 },
      {
        field: "tradeState",
        headerName: t("TradeState"),
        cellRenderer: (params) => (
          <div
            style={{
              color:
                params.value === "Approved"
                  ? "var(--theme-green)"
                  : params.value === "Rejected"
                  ? "var(--theme-red)"
                  : "var(--theme-secondary-text)",
            }}
          >
            {params.value}
          </div>
        ),
        flex: 1,
      },
    ];
  }, [product, t]);

  // Initialize table data
  useEffect(() => {
    const initializeTable = async () => {
      if (!product || !propsTrades) {
        setTableState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const mappedTrades = mapToViewModel(propsTrades);
      setTableState({
        trades: mappedTrades,
        isLoading: false,
      });
    };

    initializeTable();
  }, [
    product,
    propsTrades,
    marketDate,
    priceBasisId,
    currencyId,
    uomId,
    organizaitonId,
    mapToViewModel,
  ]);

  // Enhanced row click handler to reliably pass data to parent
  const handleRowClick = useCallback(
    (params) => {
      if (onRowClick && params.data) {
        // Find the original trade data to pass to the parent
        const originalTrade = propsTrades.find(
          (trade) => trade.tradeId === params.data.tradeId
        );

        // Use the original trade object if found, otherwise use the grid data
        onRowClick(originalTrade || params.data);
      }
    },
    [onRowClick, propsTrades]
  );

  // Row class to indicate clickable rows
  const getRowClass = useCallback(() => {
    return onRowClick ? "clickable-row" : "";
  }, [onRowClick]);

  const autoSizeGridColumns = useCallback((params) => {
    params?.columnApi?.autoSizeAllColumns?.(false);
    params?.api?.autoSizeAllColumns?.(false);
  }, []);

  if (tableState.isLoading) return <Loading />;

  return (
    <div className="trades-table">
      <ThemeableAgGrid
        className="table-submit-trades clickable"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoSizeStrategy={{
          type: "fitCellContents",
          skipHeader: false,
          defaultMinWidth: 140,
        }}
        rowData={tableState.trades}
        onRowClicked={handleRowClick}
        onFirstDataRendered={autoSizeGridColumns}
        onRowDataUpdated={autoSizeGridColumns}
        onGridSizeChanged={autoSizeGridColumns}
        getRowClass={getRowClass}
        getRowId={(params) => params.data.tradeId.toString()}
        animateRows={true}
        domLayout="autoHeight"
        allowHorizontalScroll={true}
      />
    </div>
  );
};

export default React.memo(TradesTable);
